import React from 'react';
import { Link } from 'react-router-dom';
import { TrashIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, total, getCartItemsCount } = useCart();
  const { settings } = useSettings();

  if (cartItems.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1 5H19M7 13v8a2 2 0 002 2h6a2 2 0 002-2v-8m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v4.01" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            سلة التسوق فارغة
          </h2>
          <p className="text-gray-600 mb-8">
            لم تقم بإضافة أي منتجات للسلة بعد
          </p>
          <Link to="/products" className="btn-primary">
            تصفح المنتجات
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">
        سلة التسوق ({getCartItemsCount()} عنصر)
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {cartItems.map((item) => (
              <div key={item.product.id} className="p-6 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center space-x-4 space-x-reverse">
                  {/* Product Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={item.product.images?.[0]?.url || '/placeholder-product.jpg'}
                      alt={item.product.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/products/${item.product.id}`}
                      className="text-lg font-medium text-gray-900 hover:text-primary-600"
                    >
                      {item.product.name}
                    </Link>
                    {item.product.vendor && (
                      <p className="text-sm text-gray-500 mt-1">
                        بواسطة: {item.product.vendor.storeName}
                      </p>
                    )}
                    {item.variant && (
                      <p className="text-sm text-gray-500 mt-1">
                        المتغير: {item.variant}
                      </p>
                    )}
                    <p className="text-lg font-bold text-primary-600 mt-2">
                      {item.product.price.toLocaleString()} جنيه
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                    >
                      <MinusIcon className="w-4 h-4" />
                    </button>
                    <span className="text-lg font-medium w-12 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Item Total */}
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      {(item.product.price * item.quantity).toLocaleString()} جنيه
                    </p>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.product.id)}
                    className="text-red-500 hover:text-red-700 p-2"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ملخص الطلب
            </h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">المجموع الفرعي</span>
                <span className="font-medium">{total.toLocaleString()} جنيه</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">الشحن</span>
                <span className="font-medium">{parseFloat(settings?.business?.shipping_cost || 50).toLocaleString()} جنيه</span>
              </div>
              {parseFloat(settings?.business?.tax_rate || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">الضريبة ({settings?.business?.tax_rate}%)</span>
                  <span className="font-medium">{(total * (parseFloat(settings?.business?.tax_rate || 0) / 100)).toLocaleString()} جنيه</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-gray-900">المجموع الكلي</span>
                  <span className="text-lg font-bold text-primary-600">
                    {(
                      total + 
                      parseFloat(settings?.business?.shipping_cost || 50) + 
                      (total * (parseFloat(settings?.business?.tax_rate || 0) / 100))
                    ).toLocaleString()} جنيه
                  </span>
                </div>
              </div>
            </div>

            <Link
              to="/checkout"
              className="w-full btn-primary text-center block"
            >
              إتمام الشراء
            </Link>

            <Link
              to="/products"
              className="w-full btn-outline text-center block mt-3"
            >
              متابعة التسوق
            </Link>

            {/* Security Badge */}
            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-5 h-5 text-green-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>عملية دفع آمنة ومحمية</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;